import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { startAuthentication } from '@simplewebauthn/browser';
import { api } from '../lib/api.js';

export default function Login() {
  const { login, verifyMfa, loginWithPasskey, mfaPending } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      const r = await login(email.trim(), password);
      if (r.mfaRequired) setInfo('Enter the 6-digit code from your authenticator app.');
    } catch (err) {
      if (err.data?.locked && err.data?.kind === 'soft') {
        setError(`Too many failures. Try again in ${err.data.remainingMinutes} minute(s).`);
      } else if (err.data?.locked && err.data?.kind === 'hard') {
        setError('This account is locked. Contact an admin to reset it.');
      } else if (err.data?.attemptsRemaining != null) {
        setError(`${err.message} — ${err.data.attemptsRemaining} attempts left before cooldown.`);
      } else {
        setError(err.message || 'Login failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await verifyMfa(code.trim());
    } catch (err) {
      setError(err.message || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const signInWithPasskey = async () => {
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      const options = await api.passkey.loginOptions(email.trim() || null);
      const assertion = await startAuthentication({ optionsJSON: options });
      await loginWithPasskey(assertion);
    } catch (err) {
      setError(err.message || 'Passkey sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  if (mfaPending) {
    return (
      <Centered>
        <form onSubmit={submitMfa} className="space-y-4">
          <h1 className="text-xl font-semibold text-cathay-700">Two-factor verification</h1>
          <p className="text-sm text-slate-600">
            Enter the 6-digit code from your authenticator app, or use a backup code.
          </p>
          <input
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="numeric"
            placeholder="123456"
            className="w-full border border-slate-300 rounded px-3 py-2 text-center tracking-widest text-lg"
          />
          {error && <div className="text-sm text-red-600">{error}</div>}
          <button
            disabled={busy}
            className="w-full bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white py-2 rounded"
          >
            Verify
          </button>
        </form>
      </Centered>
    );
  }

  return (
    <Centered>
      <form onSubmit={submit} className="space-y-4">
        <h1 className="text-xl font-semibold text-cathay-700">Sign in</h1>
        <p className="text-sm text-slate-500">IPT Management App — FOP admin console</p>
        <label className="block">
          <span className="text-sm text-slate-700">Email or CathayID</span>
          <input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            className="mt-1 w-full border border-slate-300 rounded px-3 py-2"
          />
        </label>
        <label className="block">
          <span className="text-sm text-slate-700">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="mt-1 w-full border border-slate-300 rounded px-3 py-2"
          />
        </label>
        {info && <div className="text-sm text-cathay-700">{info}</div>}
        {error && <div className="text-sm text-red-600">{error}</div>}
        <button
          type="submit"
          disabled={busy}
          className="w-full bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white py-2 rounded"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <button
          type="button"
          onClick={signInWithPasskey}
          disabled={busy}
          className="w-full bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 py-2 rounded text-sm"
        >
          Sign in with passkey
        </button>
      </form>
    </Centered>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-sm bg-white rounded-lg shadow-md p-6">{children}</div>
    </div>
  );
}
